import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Landmark, RefreshCw, Printer, FlaskConical, CheckCircle2, Copy } from "lucide-react";
import { formatCurrency, formatDate, API_URL } from "../../lib/utils";
import { openHtmlDocument } from "../../lib/docViewer";

const fmtRef = (r = "") => r.replace(/(\d{3})(?=\d)/g, "$1 ");

export const PaymentReferenceDialog = ({ invoice, open, onClose, headers, onPaid }) => {
  const [charge, setCharge] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !invoice) return;
    setCharge(null);
    setBusy(true);
    axios.post(`${API_URL}/invoices/${invoice.invoice_id}/payment-reference`, {}, { headers, withCredentials: true })
      .then((r) => setCharge(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Erro ao gerar referência"); onClose(); })
      .finally(() => setBusy(false));
  }, [open, invoice?.invoice_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const check = async (simulate = false) => {
    if (!charge) return;
    setBusy(true);
    try {
      const url = `${API_URL}/payments/appypay/charges/${charge.charge_id}${simulate ? "/simulate" : ""}`;
      const r = simulate ? await axios.post(url, {}, { headers, withCredentials: true }) : await axios.get(url, { headers, withCredentials: true });
      setCharge(r.data);
      if (r.data.status === "success") { toast.success(`Fatura ${invoice.invoice_number} paga por referência`); onPaid?.(); }
      else if (!simulate) toast.info("Pagamento ainda não recebido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao verificar");
    } finally {
      setBusy(false);
    }
  };

  const print = () => {
    if (!charge) return;
    openHtmlDocument(`<html><head><title>Referência ${invoice.invoice_number}</title><style>body{font-family:monospace;padding:24px;text-align:center}h2{margin:0 0 4px}table{margin:16px auto;border-collapse:collapse}td{padding:6px 12px;border:1px solid #000;font-size:16px}.big{font-size:22px;font-weight:bold}</style></head><body>
      <h2>REFERÊNCIA DE PAGAMENTO</h2><p>Fatura ${invoice.invoice_number}</p>
      <table><tr><td>Entidade</td><td class="big">${charge.entity || "—"}</td></tr><tr><td>Referência</td><td class="big">${fmtRef(charge.reference_number)}</td></tr><tr><td>Valor</td><td class="big">${formatCurrency(charge.amount)}</td></tr><tr><td>Validade</td><td>${charge.due_date ? new Date(charge.due_date).toLocaleDateString("pt-PT") : "—"}</td></tr></table>
      <p style="font-size:12px">Pague em qualquer ATM Multicaixa, Internet Banking ou Multicaixa Express.${charge.simulated ? "<br><b>SIMULADO — sem valor real</b>" : ""}</p>
      <script>window.print()</script></body></html>`, { title: `Referência ${invoice.invoice_number}` });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-reference-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="w-5 h-5 text-emerald-500" /> Referência de pagamento · {invoice?.invoice_number}</DialogTitle>
          <DialogDescription>O cliente paga em ATM, Internet Banking ou Multicaixa Express. A fatura fica paga automaticamente quando a AppyPay confirmar.</DialogDescription>
        </DialogHeader>
        {charge ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              {charge.simulated && <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30" data-testid="reference-simulated-badge"><FlaskConical className="w-3 h-3 mr-1" /> SIMULADO</Badge>}
              <Badge className={charge.status === "success" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" : charge.status === "pending" ? "bg-sky-500/15 text-sky-600 border-sky-500/30" : "bg-red-500/15 text-red-600 border-red-500/30"} data-testid="reference-status-badge">
                {charge.status === "success" ? "PAGA" : charge.status === "pending" ? "A aguardar pagamento" : charge.status}
              </Badge>
            </div>
            <div className="rounded-xl border-2 border-dashed p-4 grid grid-cols-2 gap-3 text-center">
              <div><p className="text-[11px] uppercase text-muted-foreground">Entidade</p><p className="font-mono text-2xl font-black" data-testid="reference-entity">{charge.entity || "—"}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Referência</p><p className="font-mono text-2xl font-black" data-testid="reference-number">{fmtRef(charge.reference_number)}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Valor</p><p className="font-mono text-xl font-bold text-emerald-600">{formatCurrency(charge.amount)}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Validade</p><p className="font-semibold">{charge.due_date ? new Date(charge.due_date).toLocaleDateString("pt-PT") : "—"}</p></div>
            </div>
            {charge.status === "success" && <p className="text-sm text-emerald-600 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Pagamento recebido {charge.completed_at && `em ${formatDate(charge.completed_at)}`}</p>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">A gerar referência…</p>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          {charge && (
            <>
              <Button variant="outline" size="sm" onClick={() => navigator.clipboard?.writeText(`Entidade ${charge.entity} · Referência ${charge.reference_number} · ${formatCurrency(charge.amount)}`).then(() => toast.success("Copiado"))} data-testid="reference-copy-btn"><Copy className="w-4 h-4 mr-1" /> Copiar</Button>
              <Button variant="outline" size="sm" onClick={print} data-testid="reference-print-btn"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
              {charge.status === "pending" && <Button variant="outline" size="sm" onClick={() => check(false)} disabled={busy} data-testid="reference-check-btn"><RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Verificar</Button>}
              {charge.status === "pending" && (charge.simulated || charge.env === "sandbox") && <Button size="sm" onClick={() => check(true)} disabled={busy} data-testid="reference-simulate-btn"><FlaskConical className="w-4 h-4 mr-1" /> Simular pagamento</Button>}
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
